import React, { useEffect, useRef, useCallback } from 'react';

export default function MysticalLogo() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isHoveredRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    // Set canvas size - increased for better visual balance
    const size = 160;
    canvas.width = size * 2;
    canvas.height = size * 2;
    canvas.style.width = size + 'px';
    canvas.style.height = size + 'px';

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Cube rotation state
    let rotationX = 0;
    let rotationY = 0;
    let targetRotationX = 0;
    let targetRotationY = 0;

    // Spark ring particles
    interface SparkParticle {
      angle: number;
      radius: number;
      speed: number;
      size: number;
      opacity: number;
      color: string;
    }

    const sparkRings: SparkParticle[][] = [[], [], []]; // 3 rings
    let animationFrameId: number;

    // Create spark particles for a ring
    const createSparkRing = (ringIndex: number) => {
      const particleCount = 12 + ringIndex * 4;
      const colors = [
        'rgba(14, 165, 233, ', // cyan
        'rgba(139, 92, 246, ', // purple
        'rgba(255, 215, 0, ', // gold
      ];

      for (let i = 0; i < particleCount; i++) {
        sparkRings[ringIndex].push({
          angle: (i / particleCount) * Math.PI * 2,
          radius: 60 + ringIndex * 20, // Increased for larger canvas
          speed: 0.02 + ringIndex * 0.01,
          size: 2 + Math.random() * 2,
          opacity: 0,
          color: colors[ringIndex % colors.length],
        });
      }
    };

    // Initialize rings
    createSparkRing(0);
    createSparkRing(1);
    createSparkRing(2);

    // Draw 3D cube
    const drawCube = (ctx: CanvasRenderingContext2D, rotX: number, rotY: number) => {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const cubeSize = 35; // Increased cube size for larger canvas

      // Cube vertices
      const vertices = [
        [-1, -1, -1],
        [1, -1, -1],
        [1, 1, -1],
        [-1, 1, -1],
        [-1, -1, 1],
        [1, -1, 1],
        [1, 1, 1],
        [-1, 1, 1],
      ];

      // Rotate and project vertices
      const projected = vertices.map(([x, y, z]) => {
        // Rotate around Y axis
        const x1 = x * Math.cos(rotY) - z * Math.sin(rotY);
        const z1 = x * Math.sin(rotY) + z * Math.cos(rotY);

        // Rotate around X axis
        const y1 = y * Math.cos(rotX) - z1 * Math.sin(rotX);
        const z2 = y * Math.sin(rotX) + z1 * Math.cos(rotX);

        // Simple perspective projection
        const scale = 2 / (3 - z2 * 0.3);
        return {
          x: centerX + x1 * cubeSize * scale,
          y: centerY + y1 * cubeSize * scale,
          z: z2,
        };
      });

      // Draw edges with gradient
      const edges = [
        [0, 1],
        [1, 2],
        [2, 3],
        [3, 0], // back face
        [4, 5],
        [5, 6],
        [6, 7],
        [7, 4], // front face
        [0, 4],
        [1, 5],
        [2, 6],
        [3, 7], // connecting edges
      ];

      // Sort edges by average z for proper depth rendering
      const sortedEdges = edges
        .map(([i, j]) => ({
          start: projected[i],
          end: projected[j],
          z: (projected[i].z + projected[j].z) / 2,
        }))
        .sort((a, b) => a.z - b.z);

      sortedEdges.forEach(({ start, end, z }) => {
        const alpha = 0.3 + (z + 1) * 0.35;
        const gradient = ctx.createLinearGradient(start.x, start.y, end.x, end.y);
        gradient.addColorStop(0, `rgba(14, 165, 233, ${alpha})`);
        gradient.addColorStop(0.5, `rgba(139, 92, 246, ${alpha})`);
        gradient.addColorStop(1, `rgba(14, 165, 233, ${alpha})`);

        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
      });

      // Draw vertices as glowing dots
      projected.forEach(point => {
        const alpha = 0.5 + (point.z + 1) * 0.25;
        const gradient = ctx.createRadialGradient(point.x, point.y, 0, point.x, point.y, 4);
        gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha})`);
        gradient.addColorStop(0.5, `rgba(139, 92, 246, ${alpha * 0.5})`);
        gradient.addColorStop(1, 'rgba(139, 92, 246, 0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(point.x, point.y, 4, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    // Draw spark ring
    const drawSparkRing = (ring: SparkParticle[], expansion: number) => {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      ring.forEach(spark => {
        const x = centerX + Math.cos(spark.angle) * (spark.radius + expansion);
        const y = centerY + Math.sin(spark.angle) * (spark.radius + expansion);

        // Spark glow
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, spark.size * 3);
        gradient.addColorStop(0, spark.color + spark.opacity + ')');
        gradient.addColorStop(0.5, spark.color + spark.opacity * 0.5 + ')');
        gradient.addColorStop(1, spark.color + '0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, spark.size * 3, 0, Math.PI * 2);
        ctx.fill();

        // Spark core
        ctx.fillStyle = spark.color + spark.opacity + ')';
        ctx.beginPath();
        ctx.arc(x, y, spark.size * 0.5, 0, Math.PI * 2);
        ctx.fill();

        // Trail
        ctx.strokeStyle = spark.color + spark.opacity * 0.3 + ')';
        ctx.lineWidth = spark.size * 0.3;
        ctx.beginPath();
        ctx.moveTo(x, y);
        const trailAngle = spark.angle - Math.PI / 4;
        const trailLength = 8;
        ctx.lineTo(x + Math.cos(trailAngle) * trailLength, y + Math.sin(trailAngle) * trailLength);
        ctx.stroke();
      });
    };

    // Animation state
    let ringExpansion = 0;
    let ringOpacity = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update rotation
      if (isHoveredRef.current) {
        targetRotationY += 0.05;
        targetRotationX += 0.02;
      } else {
        targetRotationY += 0.02;
        targetRotationX += 0.005;
      }

      rotationX += (targetRotationX - rotationX) * 0.1;
      rotationY += (targetRotationY - rotationY) * 0.1;

      // Draw cube
      drawCube(ctx, rotationX, rotationY);

      // Handle spark rings on hover
      if (isHoveredRef.current) {
        ringExpansion += 2;
        ringOpacity = Math.min(1, ringOpacity + 0.05);

        if (ringExpansion > 60) {
          ringExpansion = 0;
        }
      } else {
        ringOpacity = Math.max(0, ringOpacity - 0.05);
      }

      // Draw spark rings
      sparkRings.forEach((ring, index) => {
        const expansion = ringExpansion - index * 15;
        if (expansion > 0) {
          ring.forEach(spark => {
            spark.opacity = ringOpacity * (1 - expansion / 80);
          });
          drawSparkRing(ring, expansion);
        }
      });

      // Update spark angles
      sparkRings.forEach(ring => {
        ring.forEach(spark => {
          spark.angle += spark.speed;
        });
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseEnter = useCallback(() => { isHoveredRef.current = true; }, []);
  const handleMouseLeave = useCallback(() => { isHoveredRef.current = false; }, []);

  return (
    <div
      ref={containerRef}
      className='relative inline-block cursor-pointer'
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Glow effect - uses CSS transition, no JS re-render needed */}
      <div
        className='absolute inset-0 rounded-2xl blur-xl opacity-30 hover:opacity-80 transition-opacity duration-300'
        style={{
          background:
            'radial-gradient(circle, rgba(14, 165, 233, 0.4) 0%, rgba(139, 92, 246, 0.2) 50%, transparent 70%)',
        }}
      />

      {/* Canvas for cube and sparks */}
      <canvas ref={canvasRef} className='relative' style={{ display: 'block' }} />
    </div>
  );
}
